import { Drawer, DrawerContent, Portal } from '@chakra-ui/react';
import { Modal, ModalBody, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { SearchDataItemProps, SearchDataProps, SearchDialog } from '@components/search-dialog';
import { useSnackbar } from '@components/snackbar';
import { css, Global } from '@emotion/react';
import { Navigation } from '@sketch-note/design-system/src/shared/site-settings';
import cn from 'classnames';
import { useRouter } from 'next/router';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { API, endpoints } from 'src/api';
import Analytics from 'src/api/services/analytics/analytics';
import { FileExplore, FileExploreState, useFileExploreStore } from 'src/components/file-explore';
import { QuickHelp } from 'src/components/quick-help';
import { Settings } from 'src/components/settings';
import { SideBar } from 'src/components/sidebar';
import { SaveTemplate } from 'src/components/templates';
import { Notifications } from 'src/features/notifications';
import { useAuth } from 'src/shared/hooks/use-auth';
import { useLayout } from 'src/shared/hooks/use-layout';
import { TOGGLE_SIDEBAR } from 'src/store/types';
import { getGithubAuthUrl, getSlackAuthUrl } from 'src/utils/external-auth-url';

import { FileNavbar } from '../components/FileNavbar';

const { useEffect, useState, useRef, useCallback } = React;

export const FileLayout = ({
  children,
  fileId,
}: {
  children: JSX.Element | JSX.Element[];
  fileId: string;
}): JSX.Element => {
  const authData = useAuth((s) => s.userDetails);
  const {
    currentSettingsMenu,
    isHelpOpened,
    isNotificationsOpened,
    isSettingsOpened,
    isSidebarOpened,
    onCloseHelp,
    onCloseNotifications,
    onCloseSettings,
    onOpenNotificationSettings,
    onOpenSettings,
    onSearchClose,
    onSearchOpen,
    isSearchOpen,
  } = useLayout();
  const [searchFilterValue, setSearchFilterValue] = useState({
    label: 'All results',
    value: 'all-results',
  });
  const {
    isExploreOpen,
    isFileOption,
    hasExploreMenu,
    isSaveTemplateOpen,
    closeExploreMenu,
    setSlackAuthUrl,
    setGithubAuthUrl,
    setGithubAccess,
    setGithubRepos,
    setGithubUserRepos,
    setOnGitRepoSelect,
    setOnRemoveGithubRepo,
    setIsSaveTemplateOpen,
    setFileOption,
    setIsExploreOpen,
    setHasExpolreMenu,
  } = useFileExploreStore();

  const getSearchResult = async (
    search: string,
    start: number,
    limit: number
  ): Promise<SearchDataProps> => {
    Analytics.track('Search', {
      'Search Word': search,
      'Search Source': 'Main Search Tab',
    });
    const result =
      router.query.mode === 'a'
        ? await API.get(`${endpoints.search.url}?search=${search}&start=${start}&limit=${limit}`)
        : await API.get(
            `${endpoints.search.filesearchurl}?search=${search}&start=${start}&limit=${limit}`
          );

    const retval = result.data;

    return retval;
  };
  const dispatch = useDispatch();
  const router = useRouter();
  const [openSnackbar] = useSnackbar();
  const handleProgressSorting = (e) => {
    setSearchFilterValue({ label: e.label, value: e.value });
  };
  const getRecentSearches = async (): Promise<SearchDataItemProps[]> => {
    const result =
      router.query.mode === 'a'
        ? await API.get(`${endpoints.search.recent}`)
        : await API.get(`${endpoints.search.filesearchrecent}`);
    return result.data;
  };
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;
  const ref = useRef<HTMLDivElement>(null);

  const handleRepoSelection = React.useCallback(async ({ repo }) => {
    try {
      const payload = {
        type: 'github',
        src: fileId,
        srcType: 'board',
        payload: repo,
      };
      const response = await API.post('/integrations', payload);
      closeExploreMenu();
      if (response?.data?.success) {
        openSnackbar({
          text: 'Github repo successfully integrated',
          snackType: 'standard',
          position: 'bottom-center',
        });
      } else {
        openSnackbar({
          text: 'There was an error in integrating the repo',
          snackType: 'standard',
          position: 'bottom-center',
        });
      }
    } catch (error) {
      closeExploreMenu();
      openSnackbar({
        text: 'There was an error in integrating the repo',
        snackType: 'standard',
        position: 'bottom-center',
      });
    }
  }, []);

  const onRepoRemoval: FileExploreState['onRemoveGithubRepo'] = React.useCallback(
    async ({ repoName }) => {
      try {
        const response = await API.delete(
          `/integrations?type=github&name=${repoName}&file=${fileId}`
        );

        if (response.data.success) {
          closeExploreMenu();
          openSnackbar({
            text: 'The github repo was removed from the board',
            snackType: 'standard',
            position: 'bottom-center',
          });
          return { status: 'success' };
        }
        return { status: 'failed' };
      } catch (error) {
        closeExploreMenu();
        openSnackbar({
          text: 'There was an error in removing the repo',
          snackType: 'standard',
          position: 'bottom-center',
        });
        return { status: 'failed' };
      }
    },
    []
  );

  const handleSelectSearch = (item: SearchDataItemProps, postSearch: boolean) => {
    if (postSearch) {
      router.query.mode === 'a'
        ? API.post(endpoints.search.save, item)
        : API.post(endpoints.search.filesearchsave, item);
    }
    if (router.query.mode === 'a') {
      router.push(`/${mode}/${orgId}/lessons/${item.slug}`);
    } else {
      router.push(`/${mode}/${orgId}/files/${item.id}`);
    }
  };

  useEffect(() => {
    if (fileId && isExploreOpen) {
      const orgId = router.query.orgId as string;
      const viewId = router.query.view as string;
      setSlackAuthUrl(getSlackAuthUrl('board', fileId, orgId, viewId));
      setGithubAuthUrl(getGithubAuthUrl('board', fileId, orgId, viewId));
      API.get('/integrations/?type=github&data=check').then((git) => {
        if (git?.data?.data?._id) {
          setGithubAccess(true);
          API.get('/integrations/?type=github&data=repos').then(({ data: repos }) => {
            setGithubRepos(repos.data.data);
          });
          API.get(`/integrations?type=github&data=file&id=${fileId}`).then(({ data: repos }) => {
            setGithubUserRepos(repos.data);
          });
        } else {
          setGithubAccess(false);
          setGithubRepos([]);
          setGithubUserRepos([]);
        }
      });
      setOnGitRepoSelect(handleRepoSelection);
      setOnRemoveGithubRepo(onRepoRemoval);
    }
  }, [fileId, isExploreOpen]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        if (isExploreOpen) {
          setHasExpolreMenu(false);
        }
      }
    };
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [isExploreOpen]);

  function handleToggleSideBar() {
    dispatch({ type: TOGGLE_SIDEBAR });
  }

  return (
    <div
      className={cn('', {
        'flex flex-1 w-full h-full overflow-auto': true,
        'overflow-hidden': router.query.type === 'form',
      })}
    >
      <div className={cn({ 'h-screen ': true, 'w-296': isSidebarOpened })}>
        <SideBar
          handleToggleSideBar={handleToggleSideBar}
          isOpen={isSidebarOpened}
          catalogHref={Navigation.Catalogue.href}
        />
      </div>

      <div
        className={cn({
          'w-full h-full flex-col overflow-hidden': true,
        })}
      >
        <div className={cn({ 'z-20 bg-white h-50': true })}>
          <FileNavbar />
        </div>

        {isExploreOpen ? (
          <div
            ref={ref}
            style={{ minWidth: '38rem', maxWidth: '38rem', height: 'calc(100vh - 50px)' }}
            className="absolute pb-10 right-0 shadow-xl mt-2 px-20 z-200 border-l bg-white"
          >
            <FileExplore />
          </div>
        ) : null}

        <Drawer
          size="lg"
          isOpen={isNotificationsOpened}
          placement="right"
          onClose={onCloseNotifications}
        >
          <Portal>
            <DrawerContent className="overflow-auto">
              {authData?.authentication === 'authorised' ? <Notifications /> : <div />}
            </DrawerContent>
          </Portal>
        </Drawer>

        <Modal isOpen={isSettingsOpened} onClose={onCloseSettings}>
          <ModalOverlay />
          <ModalContent
            width="auto"
            maxWidth="auto"
            margin="auto"
            borderRadius="0.8rem"
            className="static rounded-lg"
          >
            <ModalBody className="flex items-center justify-center text-left" padding="0px">
              <Settings
                currentMenu={currentSettingsMenu}
                isOpen={isSettingsOpened}
                onClose={onCloseSettings}
              />
            </ModalBody>
          </ModalContent>
        </Modal>

        <Drawer size="lg" isOpen={isHelpOpened} placement="right" onClose={onCloseHelp}>
          <DrawerContent className="overflow-auto">
            <QuickHelp onClose={onCloseHelp} onClickReset={onOpenSettings} />
          </DrawerContent>
        </Drawer>

        {children}
      </div>
      <Modal isOpen={isSearchOpen} onClose={onSearchClose}>
        <ModalOverlay />
        <ModalContent
          maxWidth="70rem"
          marginTop="20rem"
          borderRadius="0.8rem"
          className="rounded-lg"
        >
          <ModalBody className="flex items-center justify-center text-left" padding="0px">
            <SearchDialog
              getRecentSearches={getRecentSearches}
              getSearchResult={getSearchResult}
              onClose={onSearchClose}
              sortMenuOptions={[
                { label: 'Best Matches', value: 'best-matches' },
                { label: 'Last edited : Newest first', value: 'newest-update-first' },
                { label: 'Last edited : Oldest first', value: 'oldest-update-first' },
                { label: 'Created : Newest first', value: 'newest-created-first' },
                { label: 'Created : Oldest first', value: 'oldest-created-first' },
              ]}
              filterMenuOptions={[
                { label: 'All results', value: 'all-results' },
                { label: 'Lessons', value: 'lessons' },
                { label: 'Creations', value: 'creations' },
                { label: 'Catalog', value: 'catalog' },
                { label: 'Docs', value: 'docs' },
                { label: 'Kanban', value: 'kanban' },
                { label: 'Calendar', value: 'calendar' },
              ]}
              sortTxt="Sort"
              filterText=""
              activeSortValue={{ label: '', value: '' }}
              activeFilterValue={{
                label: searchFilterValue.label,
                value: searchFilterValue.value,
              }}
              onClickSortValue={handleProgressSorting}
              onSelectSearchResult={handleSelectSearch}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
      <Modal
        isOpen={isSaveTemplateOpen}
        onClose={() => {
          setIsSaveTemplateOpen(false);
        }}
      >
        <ModalOverlay />
        <ModalContent
          width="auto"
          maxWidth="auto"
          margin="auto"
          borderRadius="0.8rem"
          className="static rounded-lg"
        >
          <ModalBody className="flex items-center justify-center text-left" padding="0px">
            <SaveTemplate
              fileId={fileId}
              onClose={() => {
                setIsSaveTemplateOpen(false);
              }}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
      <Global
        styles={css`
          body {
            overflow: hidden;
          }
          html,
          body {
            overscroll-behavior: none;
          }
        `}
      />
    </div>
  );
};
